/**
 * Rebalance questionnaire URLs and entry `source` query values.
 *
 * Belongs here: path constants, profile URL after submit, and parse of entry `source`.
 * Does not belong here: navigation side effects.
 */

export const REBALANCE_QUESTIONNAIRE_ROUTES = {
  root: '/allocation/rebalance-questionnaire',
  riskProfile: '/allocation/rebalance-questionnaire/risk-profile'
} as const

/** Native screen that opened this webview — query `source`. */
export const RebalanceQuestionnaireSource = {
  PORTFOLIO_DASHBOARD: 'PORTFOLIO_DASHBOARD',
  ELITE_LANDING_DASHBOARD: 'ELITE_LANDING_DASHBOARD',
  REBALANCE_PLAN_PAGE: 'REBALANCE_PLAN_PAGE'
} as const

export type RebalanceQuestionnaireSource =
  (typeof RebalanceQuestionnaireSource)[keyof typeof RebalanceQuestionnaireSource]

const SOURCE_VALUES = new Set<string>(Object.values(RebalanceQuestionnaireSource))

/** Parses `?source=` for this feature; null when missing/unknown. */
export function parseRebalanceQuestionnaireSource(
  value: string | null
): RebalanceQuestionnaireSource | null {
  if (value != null && SOURCE_VALUES.has(value)) return value as RebalanceQuestionnaireSource
  return null
}

const FROM_APP = { key: 'from', value: 'APP' } as const

/** True when the native app opened this webview (`from=APP`). */
export function isOpenedFromApp(search: string): boolean {
  return new URLSearchParams(search).get(FROM_APP.key) === FROM_APP.value
}

/** Profile screen. `riskProfile` is the only query. Does not copy `from=APP` from the questions route. */
export function rebalanceRiskProfilePath(riskProfile: string): string {
  const params = new URLSearchParams()
  params.set('riskProfile', riskProfile)
  return `${REBALANCE_QUESTIONNAIRE_ROUTES.riskProfile}?${params.toString()}`
}

/** Questionnaire root for Retake. Drops `riskProfile`. Keeps `from=APP` from the profile URL. */
export function rebalanceQuestionnairePath(search: string): string {
  if (!isOpenedFromApp(search)) return REBALANCE_QUESTIONNAIRE_ROUTES.root
  return `${REBALANCE_QUESTIONNAIRE_ROUTES.root}?${FROM_APP.key}=${FROM_APP.value}`
}
